import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PMPagination } from "../../../utils/PMPagination";
import { ItemStatus, dateToString } from "../../../Utils";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import {
  PencilIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";

export const InventoryCountsTable = (props) => {
  const { t } = useTranslation();
  const { itemState, onPageChange, itemsCount, page, perPage, currentCount, onItemsSelect } = props;
  const { data, isLoading, errors } = itemState;
  const navigate = useNavigate();

  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems?.length > 0 && selectedItems?.length < data?.length;
    setChecked(selectedItems?.length === data?.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    if (onItemsSelect) {
      onItemsSelect(selectedItems);
    }
  }, [selectedItems, data?.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : data);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return t("inventory.count.status.draft");
      case "pending_approval":
        return t("inventory.count.status.pending_approval");
      case "approved":
        return t("inventory.count.status.approved");
      case "completed":
        return t("inventory.count.status.completed");
      default:
        return status;
    }
  };

  const CountCard = ({ item }) => {
    return (
      <div
        className={`shadow sm:overflow-hidden sm:rounded-md hover:shadow-lg transition-shadow duration-200 ${
          selectedItems.includes(item) ? "ring-2 ring-blue-400" : ""
        }`}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className="flex-shrink-0 cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={() => navigate("/dashboard/inventory/counts/" + item?.id)}
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h3
                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors duration-200"
                    onClick={() => navigate("/dashboard/inventory/counts/" + item?.id)}
                  >
                    {item?.count_number || `#${item?.id}`}
                  </h3>
                  {item?.status && (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        item.status
                      )}`}
                    >
                      {getStatusLabel(item.status)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{item?.title || t("inventory.count.no_title")}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {item?.active && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  {t("Active")}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Date */}
            {item?.date && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {t("inventory.count.date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.date)}
                </dd>
              </div>
            )}

            {/* Created Date */}
            {item?.created_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {t("Created")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.created_at)}
                </dd>
              </div>
            )}

            {/* Updated Date */}
            {item?.updated_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {t("Updated")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.updated_at)}
                </dd>
              </div>
            )}
          </div>

          {/* Description */}
          {item?.description && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">{t("Description")}</dt>
              <dd className="mt-1 text-sm text-gray-900 line-clamp-3">
                {item.description}
              </dd>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
                value={item.id}
                checked={selectedItems.includes(item)}
                onChange={(e) => {
                  setSelectedItems(
                    e.target.checked
                      ? [...selectedItems, item]
                      : selectedItems.filter((p) => p !== item)
                  );
                }}
              />
              <span className="text-sm text-gray-500">{t("Select")}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate("/dashboard/inventory/counts/" + item.id)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                {t("Edit")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Stats = () => {
    return (
      <>
        {data && data.length > 0 && (
          <div className="space-y-4 mt-5">
            {data?.map((item) => (
              <CountCard key={item?.id} item={item} />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
      {/* Selection bar */}
      {selectedItems?.length > 0 && (
        <div className="relative px-5 top-0 flex h-12 items-center space-x-3 bg-blue-400 rounded-xl mb-4 text-white">
          <div className="flex w-full items-center justify-between">
            <div className="">
              <span>{selectedItems.length} {t("selected")}</span>
            </div>
            <div className="px-2 py-2">
              <button
                type="button"
                className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
              >
                {t("Edit")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select all checkbox */}
      {data && data.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
              ref={checkbox}
              checked={checked}
              onChange={toggleAll}
            />
            <label className="ml-2 text-sm font-medium text-gray-700">
              {t("Select all")}
            </label>
          </div>
        </div>
      )}

      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          {isLoading ? (
            <>
              <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-16 bg-gray-200 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-16 bg-gray-200 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </>
          ) : errors ? (
            <div className="text-center py-8 text-red-500">
              <div className="text-lg">{t("inventory.count.could_not_load")}</div>
            </div>
          ) : data && data.length > 0 ? (
            <Stats />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-lg">{t("inventory.count.no_counts_found")}</div>
              <div className="text-sm">{t("inventory.count.create_new")}</div>
            </div>
          )}
        </div>
      </div>

      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage || 25}
        rowsCount={itemsCount}
        totalCount={itemsCount}
        currentCount={currentCount || data?.length}
        showCount={true}
      />
    </div>
  );
};

InventoryCountsTable.defaultProps = {
  page: 1,
  itemState: { data: [], isLoading: false, errors: null },
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
  onItemsSelect: () => {},
};
